import { useActivityLogs } from '@/hooks/useActivityLogs'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Upload, Edit, Trash2, Activity, AlertCircle, Link, PlusCircle, ChevronRight, Layers, CheckCircle, MessageSquare } from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import type { ActivityLog, ActivityActionType, Clip } from '@/types/database'
import { useState, useMemo } from 'react'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useAuth } from '@/contexts/AuthContext'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useProjects } from '@/hooks/useProjects'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ComparisonActivityDetails } from './ComparisonActivityDetails'
import { ClipDetailSheet } from './ClipDetailSheet'
import { useClips } from '@/hooks/useClips'

interface ActivityFeedProps {
  projectId?: string | null
  showProjectFilter?: boolean
  showHeader?: boolean
}

interface ActivityLogWithUser extends ActivityLog {
  user?: {
    full_name: string | null
  }
}

const ACTION_CONFIG: Record<ActivityActionType, { label: string; icon: typeof Upload; color: string }> = {
  clip_import: {
    label: 'imported',
    icon: Upload,
    color: 'text-green-500',
  },
  clip_update: {
    label: 'updated',
    icon: Edit,
    color: 'text-blue-500',
  },
  clip_delete: {
    label: 'deleted',
    icon: Trash2,
    color: 'text-red-500',
  },
  clip_relationship_create: {
    label: 'linked clips',
    icon: Link,
    color: 'text-purple-500',
  },
  clip_relationship_delete: {
    label: 'unlinked clips',
    icon: Link,
    color: 'text-gray-500',
  },
  project_update: {
    label: 'updated project',
    icon: Edit,
    color: 'text-blue-500',
  },
  project_create: {
    label: 'created project',
    icon: Activity,
    color: 'text-green-500',
  },
  sequence_create: {
    label: 'created sequence',
    icon: Activity,
    color: 'text-green-500',
  },
  sequence_update: {
    label: 'updated sequence',
    icon: Edit,
    color: 'text-blue-500',
  },
  otio_clip_link: {
    label: 'linked clips from OTIO',
    icon: Link,
    color: 'text-blue-500',
  },
  otio_clip_create: {
    label: 'created clips from OTIO',
    icon: PlusCircle,
    color: 'text-green-500',
  },
  otio_timeline_import: {
    label: 'imported OTIO timeline',
    icon: Upload,
    color: 'text-purple-500',
  },
  vfx_shots_detected: {
    label: 'detected VFX shots',
    icon: Layers,
    color: 'text-orange-500',
  },
  commit_delete: {
    label: 'deleted commit',
    icon: Trash2,
    color: 'text-red-500',
  },
  transcode_upload: {
    label: 'uploaded transcode',
    icon: Upload,
    color: 'text-purple-500',
  },
  transcode_complete: {
    label: 'completed transcode',
    icon: CheckCircle,
    color: 'text-green-500',
  },
  transcode_delete: {
    label: 'deleted transcode',
    icon: Trash2,
    color: 'text-red-500',
  },
  timeline_comparison: {
    label: 'compared commits',
    icon: Layers,
    color: 'text-cyan-500',
  },
  comment_created: {
    label: 'added comment',
    icon: MessageSquare,
    color: 'text-blue-500',
  },
  comment_deleted: {
    label: 'deleted comment',
    icon: Trash2,
    color: 'text-red-500',
  },
  bulk_tag_update: {
    label: 'updated clip tags',
    icon: Edit,
    color: 'text-blue-500',
  },
  bulk_status_update: {
    label: 'updated clip status',
    icon: Edit,
    color: 'text-blue-500',
  },
}

interface ActivityDetailsDialogProps {
  activity: ActivityLogWithUser | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ActivityDetailsDialog({ activity, open, onOpenChange }: ActivityDetailsDialogProps) {
  const [clipDetailOpen, setClipDetailOpen] = useState(false)
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'default' | 'tag' | 'name'>('default')

  // Fetch clips to get tag information for OTIO activities
  const { data: allClips } = useClips(activity?.project_id)
  const clips = (allClips || []) as Clip[]

  if (!activity) return null

  const config = ACTION_CONFIG[activity.action_type]
  const Icon = config?.icon || Activity
  const metadata = activity.action_metadata || {}

  const handleViewClip = (clipId: string) => {
    setSelectedClipId(clipId)
    setClipDetailOpen(true)
  }

  // Helper to get tags for a clip by NLE unique ID
  const getClipTags = (nleUniqueId?: string): string[] => {
    if (!nleUniqueId) return []
    const clip = clips.find(c => c.nle_unique_id === nleUniqueId)
    return clip?.tags || []
  }

  // Generic sort and group function for OTIO clips
  const sortAndGroupClips = (clips: any[]): Array<{ tag: string | null; items: any[] }> => {
    if (sortBy === 'default') {
      return [{ tag: null, items: clips }]
    }

    const sorted = [...clips].sort((a, b) => {
      if (sortBy === 'tag') {
        const aTags = getClipTags(a.nle_unique_id)
        const bTags = getClipTags(b.nle_unique_id)
        const aTag = aTags[0] || 'Untagged'
        const bTag = bTags[0] || 'Untagged'
        return aTag.localeCompare(bTag)
      } else if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '')
      }
      return 0
    })

    if (sortBy === 'tag') {
      // Group by tag combination (all tags together)
      const grouped = new Map<string, any[]>()
      sorted.forEach(item => {
        const tags = getClipTags(item.nle_unique_id)
        // Create a key from all tags joined together
        const tagKey = tags.length > 0 ? tags.join(', ') : 'Untagged'
        if (!grouped.has(tagKey)) {
          grouped.set(tagKey, [])
        }
        grouped.get(tagKey)!.push(item)
      })

      // Convert to array and sort tag names alphabetically
      return Array.from(grouped.entries())
        .sort(([a], [b]) => {
          // Put "Untagged" last
          if (a === 'Untagged') return 1
          if (b === 'Untagged') return -1
          return a.localeCompare(b)
        })
        .map(([tag, items]) => ({ tag, items }))
    }

    return [{ tag: null, items: sorted }]
  }

  const renderDetails = () => {
    switch (activity.action_type) {
      case 'otio_timeline_import': {
        const linkedClips = metadata.linked_clips || []
        const createdClips = metadata.created_clips || []
        const totalClips = metadata.total_clips || 0
        const clipsLinked = metadata.clips_linked || 0
        const clipsCreated = metadata.clips_created || 0

        const sortAndGroupClipsForType = (clips: any[]) => sortAndGroupClips(clips)

        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Total Clips</p>
                <p className="font-medium">{totalClips}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Linked</p>
                <p className="font-medium text-blue-600">{clipsLinked}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium text-green-600">{clipsCreated}</p>
              </div>
              <div className="col-span-3">
                <p className="text-muted-foreground">Commit Message</p>
                <p className="font-medium">{metadata.commit_message || 'N/A'}</p>
              </div>
              <div className="col-span-3">
                <p className="text-muted-foreground">Commit Number</p>
                <p className="font-medium">#{metadata.commit_number || 'N/A'}</p>
              </div>
            </div>

            {/* Sort Filter */}
            {(linkedClips.length > 0 || createdClips.length > 0) && (
              <div className="flex items-center gap-2 pb-2 border-b">
                <label className="text-sm font-medium">Sort by:</label>
                <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="tag">Tag</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Linked Clips Section */}
            {linkedClips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-blue-600">
                  Linked Clips ({linkedClips.length})
                </h4>
                <div className="space-y-4">
                  {sortAndGroupClipsForType(linkedClips).map(({ tag, items }) => (
                    <div key={tag || 'linked-default'} className="space-y-2">
                      {tag && (
                        <h5 className="font-medium text-sm border-b pb-1 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                          {tag}
                        </h5>
                      )}
                      {items.map((clip: any, idx: number) => (
                        <div
                          key={idx}
                          className="text-sm p-2 rounded border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium flex-1">
                              {clip.name}
                              {clip.usage_count && clip.usage_count > 1 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (used {clip.usage_count} times)
                                </span>
                              )}
                            </p>
                            {clip.clip_id && (
                              <button
                                onClick={() => handleViewClip(clip.clip_id)}
                                className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                              >
                                View Clip →
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                            {clip.tape_name && (
                              <div>
                                <span className="font-medium">Tape:</span> {clip.tape_name}
                              </div>
                            )}
                            {clip.source_timecode && (
                              <div>
                                <span className="font-medium">TC:</span> {clip.source_timecode}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Created Clips Section */}
            {createdClips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-green-600">
                  New Clips ({createdClips.length})
                </h4>
                <div className="space-y-4">
                  {sortAndGroupClipsForType(createdClips).map(({ tag, items }) => (
                    <div key={tag || 'created-default'} className="space-y-2">
                      {tag && (
                        <h5 className="font-medium text-sm border-b pb-1 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                          {tag}
                        </h5>
                      )}
                      {items.map((clip: any, idx: number) => (
                        <div
                          key={idx}
                          className="text-sm p-2 rounded border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium flex-1">
                              {clip.name}
                              {clip.usage_count && clip.usage_count > 1 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (used {clip.usage_count} times)
                                </span>
                              )}
                            </p>
                            {clip.clip_id && (
                              <button
                                onClick={() => handleViewClip(clip.clip_id)}
                                className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              >
                                View Clip →
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                            {clip.tape_name && (
                              <div>
                                <span className="font-medium">Tape:</span> {clip.tape_name}
                              </div>
                            )}
                            {clip.source_timecode && (
                              <div>
                                <span className="font-medium">TC:</span> {clip.source_timecode}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'otio_clip_link':
      case 'otio_clip_create': {
        const clips = metadata.clips || []
        const isCreate = activity.action_type === 'otio_clip_create'
        const groupedClips = sortAndGroupClips(clips)

        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Total Clips in Import</p>
                <p className="font-medium">{metadata.total_clips_in_import || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{isCreate ? 'Created' : 'Linked'}</p>
                <p className={`font-medium ${isCreate ? 'text-green-600' : 'text-blue-600'}`}>
                  {metadata.clips_count || 0}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Commit Message</p>
                <p className="font-medium">{metadata.commit_message || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Commit Number</p>
                <p className="font-medium">#{metadata.commit_number || 'N/A'}</p>
              </div>
            </div>

            {/* Sort Filter */}
            {clips.length > 0 && (
              <div className="flex items-center gap-2 pb-2 border-b">
                <label className="text-sm font-medium">Sort by:</label>
                <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="tag">Tag</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Clips */}
            {clips.length > 0 ? (
              <div>
                <h4 className={`font-medium mb-2 ${isCreate ? 'text-green-600' : 'text-blue-600'}`}>
                  {isCreate ? 'New Clips' : 'Linked Clips'} ({clips.length})
                </h4>
                <div className="space-y-4">
                  {groupedClips.map(({ tag, items }) => (
                    <div key={tag || 'default'} className="space-y-2">
                      {tag && (
                        <h5 className={`font-medium text-sm border-b pb-1 ${
                          isCreate
                            ? 'text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
                            : 'text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                        }`}>
                          {tag}
                        </h5>
                      )}
                      {items.map((clip: any, idx: number) => (
                        <div
                          key={idx}
                          className={`text-sm p-2 rounded border ${
                            isCreate
                              ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                              : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium flex-1">
                              {clip.name}
                              {clip.usage_count && clip.usage_count > 1 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (used {clip.usage_count} times)
                                </span>
                              )}
                            </p>
                            {clip.clip_id && (
                              <button
                                onClick={() => handleViewClip(clip.clip_id)}
                                className={`text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity ${
                                  isCreate
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                }`}
                              >
                                View Clip →
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                            {clip.tape_name && (
                              <div>
                                <span className="font-medium">Tape:</span> {clip.tape_name}
                              </div>
                            )}
                            {clip.source_timecode && (
                              <div>
                                <span className="font-medium">TC:</span> {clip.source_timecode}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (metadata.clips_count && metadata.clips_count > 0) ? (
              <div className="p-3 bg-muted/50 rounded border border-dashed">
                <p className="text-sm text-muted-foreground">
                  Detailed clip information not available for this activity (created before detailed logging was added).
                </p>
              </div>
            ) : null}
          </div>
        )
      }

      case 'clip_import': {
        const created = metadata.created || 0
        const updated = metadata.updated || 0
        const skipped = metadata.skipped || 0
        const createdClips = metadata.created_clips || []
        const updatedClips = metadata.updated_clips || []

        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium text-green-600">{created}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Updated</p>
                <p className="font-medium text-blue-600">{updated}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Skipped</p>
                <p className="font-medium text-muted-foreground">{skipped}</p>
              </div>
            </div>

            {/* Created Clips */}
            {createdClips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-green-600">New Clips ({createdClips.length})</h4>
                <div className="space-y-2">
                  {createdClips.map((clip: any, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium flex-1">{clip.name}</p>
                        {clip.clip_id && (
                          <button
                            onClick={() => handleViewClip(clip.clip_id)}
                            className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                          >
                            View Clip →
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                        {clip.tape_name && (
                          <div>
                            <span className="font-medium">Tape:</span> {clip.tape_name}
                          </div>
                        )}
                        {clip.source_timecode && (
                          <div>
                            <span className="font-medium">TC:</span> {clip.source_timecode}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Updated Clips */}
            {updatedClips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-blue-600">Updated Clips ({updatedClips.length})</h4>
                <div className="space-y-2">
                  {updatedClips.map((clip: any, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium flex-1">{clip.name}</p>
                        {clip.clip_id && (
                          <button
                            onClick={() => handleViewClip(clip.clip_id)}
                            className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            View Clip →
                          </button>
                        )}
                      </div>
                      {clip.changes && clip.changes.length > 0 && (
                        <div className="mt-1">
                          <p className="text-xs font-medium text-muted-foreground">Changed:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {clip.changes.map((change: string, changeIdx: number) => (
                              <span
                                key={changeIdx}
                                className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
                              >
                                {change.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {clip.tape_name && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          <span className="font-medium">Tape:</span> {clip.tape_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'vfx_shots_detected': {
        const shotsDetected = metadata.shots_detected || 0
        const shotsCreated = metadata.shots_created || 0
        const shotsUpdated = metadata.shots_updated || 0
        const warnings = metadata.warnings || []
        const frameChanges = metadata.frame_changes || 0
        const commitMsg = metadata.commit_message || 'N/A'
        const shots = metadata.shots || []

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Detected</p>
                <p className="font-medium">{shotsDetected}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium text-green-600">{shotsCreated}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Updated</p>
                <p className="font-medium text-blue-600">{shotsUpdated}</p>
              </div>
            </div>

            {frameChanges > 0 && (
              <div className="p-2 bg-yellow-50 dark:bg-yellow-950/20 rounded border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                  {frameChanges} frame range change{frameChanges !== 1 ? 's' : ''} detected
                </p>
              </div>
            )}

            {warnings.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-yellow-600">Warnings</h4>
                <div className="space-y-1">
                  {warnings.map((warning: string, idx: number) => (
                    <p key={idx} className="text-sm text-yellow-600 dark:text-yellow-400">⚠️ {warning}</p>
                  ))}
                </div>
              </div>
            )}

            {shots.length > 0 ? (
              <div>
                <h4 className="font-medium mb-2">VFX Shots ({shots.length})</h4>
                <div className="space-y-2">
                  {shots.map((shot: any, idx: number) => {
                    const isCreated = shot.action === 'created'
                    return (
                      <div
                        key={idx}
                        className={`text-sm p-2 rounded border ${
                          isCreated
                            ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                            : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-medium flex-1 ${isCreated ? 'text-green-700 dark:text-green-400' : 'text-blue-700 dark:text-blue-400'}`}>
                            {shot.shot_name}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            isCreated
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                              : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          }`}>
                            {shot.action}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                          <div>
                            <span className="font-medium">Frame Range:</span> {shot.frame_range_start} - {shot.frame_range_end}
                          </div>
                          <div>
                            <span className="font-medium">Duration:</span> {shot.duration} frames
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (shotsDetected > 0) ? (
              <div className="p-3 bg-muted/50 rounded border border-dashed">
                <p className="text-sm text-muted-foreground">
                  Detailed VFX shot information not available for this activity (created before detailed logging was added).
                </p>
              </div>
            ) : null}

            <div>
              <p className="text-sm text-muted-foreground mb-1">Commit Message</p>
              <p className="font-medium">{commitMsg}</p>
            </div>
          </div>
        )
      }

      case 'clip_update': {
        const clipName = metadata.clip_name || metadata.name || 'Unknown clip'
        const changes = metadata.changes || []
        const oldValues = metadata.old_values || {}
        const newValues = metadata.new_values || {}

        // Helper to format value for display
        const formatValue = (value: any): string => {
          if (value === null || value === undefined) return '(empty)'
          if (typeof value === 'object') return JSON.stringify(value)
          if (typeof value === 'boolean') return value ? 'Yes' : 'No'
          return String(value)
        }

        return (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Clip Name</p>
              <p className="font-medium">{clipName}</p>
            </div>

            {changes.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Changes Made</h4>
                <div className="space-y-2">
                  {changes.map((field: string, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                      <p className="font-medium text-blue-600 capitalize mb-1">
                        {field.replace(/_/g, ' ')}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">From:</span>{' '}
                          <span className="font-mono">{formatValue(oldValues[field])}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">To:</span>{' '}
                          <span className="font-mono">{formatValue(newValues[field])}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'commit_delete': {
        const commitNumber = metadata.commit_number
        const commitMessage = metadata.message || 'No message'
        const deletedAt = metadata.deleted_at

        return (
          <div className="space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
              <div className="space-y-2">
                {commitNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">Commit Number</p>
                    <p className="font-medium text-red-600 dark:text-red-400">#{commitNumber}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Commit Message</p>
                  <p className="font-medium">{commitMessage}</p>
                </div>
                {deletedAt && (
                  <div>
                    <p className="text-sm text-muted-foreground">Deleted At</p>
                    <p className="text-sm">{format(new Date(deletedAt), 'PPpp')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      case 'transcode_upload': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const vendor = metadata.vendor
        const totalEvents = metadata.total_events || 0
        const createdShots = metadata.created_shots || []
        const linkedShots = metadata.linked_shots || []
        const errors = metadata.errors || []

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Transcode ID</p>
                <p className="font-medium">{transcodeId}</p>
              </div>
              {vendor && (
                <div>
                  <p className="text-muted-foreground">Vendor</p>
                  <p className="font-medium">{vendor}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Total Events</p>
                <p className="font-medium">{totalEvents}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Shots Created</p>
                <p className="font-medium text-green-600">{createdShots.length}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Shots Linked</p>
                <p className="font-medium text-blue-600">{linkedShots.length}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Errors</p>
                <p className="font-medium text-red-600">{errors.length}</p>
              </div>
            </div>

            {linkedShots.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-blue-600">Linked Shots ({linkedShots.length})</h4>
                <div className="space-y-1">
                  {linkedShots.map((shotName: string, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                      {shotName}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errors.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-red-600">Errors ({errors.length})</h4>
                <div className="space-y-1">
                  {errors.map((error: string, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'transcode_complete': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const notes = metadata.completion_notes

        return (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Transcode ID</p>
              <p className="font-medium">{transcodeId}</p>
            </div>
            {notes && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Completion Notes</p>
                <p className="font-medium">{notes}</p>
              </div>
            )}
          </div>
        )
      }

      case 'transcode_delete': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const vendor = metadata.vendor
        const unlinkedShots = metadata.unlinked_shots || []
        const unlinkedCount = metadata.unlinked_count || 0

        return (
          <div className="space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground">Transcode ID</p>
                  <p className="font-medium text-red-600 dark:text-red-400">{transcodeId}</p>
                </div>
                {vendor && (
                  <div>
                    <p className="text-sm text-muted-foreground">Vendor</p>
                    <p className="font-medium">{vendor}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Shots Unlinked</p>
                  <p className="font-medium">{unlinkedCount}</p>
                </div>
              </div>
            </div>

            {unlinkedShots.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Unlinked Shots ({unlinkedShots.length})</h4>
                <div className="space-y-1">
                  {unlinkedShots.map((shotName: string, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-gray-50 dark:bg-gray-900/20 rounded border border-gray-200 dark:border-gray-800">
                      {shotName}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'timeline_comparison': {
        const fromCommit = metadata.from_commit_number
        const toCommit = metadata.to_commit_number
        const totalChanges = metadata.total_changes || 0
        const added = metadata.added || 0
        const removed = metadata.removed || 0
        const trimmed = metadata.trimmed || 0
        const extended = metadata.extended || 0
        const vfxChanges = metadata.vfx_changes || 0
        const retranscodeRequired = metadata.retranscode_required || 0
        const comparisonId = activity.entity_id

        return (
          <div className="space-y-4">
            {/* Summary Section */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">From Commit</p>
                <p className="font-medium">#{fromCommit}</p>
              </div>
              <div>
                <p className="text-muted-foreground">To Commit</p>
                <p className="font-medium">#{toCommit}</p>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Total Changes</p>
                <p className="font-medium">{totalChanges}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Added</p>
                <p className="font-medium text-green-600">{added}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Removed</p>
                <p className="font-medium text-red-600">{removed}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Trimmed</p>
                <p className="font-medium text-blue-600">{trimmed}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Extended</p>
                <p className="font-medium text-purple-600">{extended}</p>
              </div>
            </div>

            {vfxChanges > 0 && (
              <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800">
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">VFX Shot Changes</p>
                    <p className="font-medium text-orange-600 dark:text-orange-400">{vfxChanges}</p>
                  </div>
                  {retranscodeRequired > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground">Re-transcode Required</p>
                      <p className="font-medium text-orange-600 dark:text-orange-400">{retranscodeRequired} shot{retranscodeRequired !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detailed Activity Section */}
            {comparisonId && activity.project_id && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-4 text-lg">Detailed Changes</h4>
                <ComparisonActivityDetails
                  comparisonId={comparisonId}
                  projectId={activity.project_id}
                  activityMetadata={metadata}
                  onViewClip={handleViewClip}
                />
              </div>
            )}
          </div>
        )
      }

      case 'comment_created': {
        const commentPreview = metadata.comment_preview || ''
        const commentableName = metadata.commentable_name || 'Unknown'
        const commentableType = metadata.commentable_type || 'item'
        const commentableId = metadata.commentable_id

        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground capitalize">{commentableType}</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium">{commentableName}</p>
                    {commentableId && (
                      <button
                        onClick={() => handleViewClip(commentableId)}
                        className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                      >
                        View {commentableType === 'clip' ? 'Clip' : 'Shot'} →
                      </button>
                    )}
                  </div>
                </div>
                {commentPreview && (
                  <div>
                    <p className="text-sm text-muted-foreground">Comment</p>
                    <p className="text-sm italic">"{commentPreview}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      case 'comment_deleted': {
        const commentableName = metadata.commentable_name || 'Unknown'
        const commentableType = metadata.commentable_type || 'item'
        const commentableId = metadata.commentable_id

        return (
          <div className="space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground capitalize">{commentableType}</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium">{commentableName}</p>
                    {commentableId && (
                      <button
                        onClick={() => handleViewClip(commentableId)}
                        className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                      >
                        View {commentableType === 'clip' ? 'Clip' : 'Shot'} →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      case 'bulk_tag_update': {
        const clipsCount = metadata.clips_count || 0
        const addedTags = metadata.added_tags || []
        const removedTags = metadata.removed_tags || []
        const clips = metadata.clips || []

        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Clips Updated</p>
                <p className="font-medium text-blue-600">{clipsCount}</p>
              </div>
              {addedTags.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Tags Added</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {addedTags.map((tag: string, idx: number) => (
                      <span key={idx} className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {removedTags.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Tags Removed</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {removedTags.map((tag: string, idx: number) => (
                      <span key={idx} className="inline-block px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Clips */}
            {clips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-blue-600">Updated Clips ({clips.length})</h4>
                <div className="space-y-2">
                  {clips.map((clip: any, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium flex-1">{clip.name}</p>
                        {clip.clip_id && (
                          <button
                            onClick={() => handleViewClip(clip.clip_id)}
                            className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            View Clip →
                          </button>
                        )}
                      </div>
                      {clip.tape_name && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          <span className="font-medium">Tape:</span> {clip.tape_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'bulk_status_update': {
        const clipsCount = metadata.clips_count || 0
        const status = metadata.status || 'Unknown'
        const statusType = metadata.status_type || 'Unknown'
        const clips = metadata.clips || []

        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Clips Updated</p>
                <p className="font-medium text-blue-600">{clipsCount}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <p className="font-medium">{status}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium capitalize">{statusType.replace('_', ' ')}</p>
              </div>
            </div>

            {/* Clips */}
            {clips.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-blue-600">Updated Clips ({clips.length})</h4>
                <div className="space-y-2">
                  {clips.map((clip: any, idx: number) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium flex-1">{clip.name}</p>
                        {clip.clip_id && (
                          <button
                            onClick={() => handleViewClip(clip.clip_id)}
                            className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            View Clip →
                          </button>
                        )}
                      </div>
                      {clip.tape_name && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          <span className="font-medium">Tape:</span> {clip.tape_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      default:
        return (
          <div className="text-sm text-muted-foreground">
            No additional details available
          </div>
        )
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <div className={config?.color || 'text-muted-foreground'}>
              <Icon className="h-5 w-5" />
            </div>
            <SheetTitle className="capitalize">
              {activity.action_type.replace(/_/g, ' ')}
            </SheetTitle>
          </div>
          <SheetDescription>
            {format(new Date(activity.created_at), 'PPpp')}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {renderDetails()}
        </div>
      </SheetContent>

      {/* Clip Detail Sheet - Opens on the right side */}
      {activity.project_id && (
        <ClipDetailSheet
          open={clipDetailOpen}
          onOpenChange={setClipDetailOpen}
          clipId={selectedClipId}
          projectId={activity.project_id}
        />
      )}
    </Sheet>
  )
}

function ActivityItem({ activity, projectName }: { activity: ActivityLogWithUser; projectName?: string }) {
  const [showDetails, setShowDetails] = useState(false)
  const config = ACTION_CONFIG[activity.action_type]
  const Icon = config?.icon || Activity
  const { track } = useAnalytics()
  const { user: currentUser } = useAuth()

  const handleActivityClick = () => {
    // Track activity item click
    track('activity_item_clicked', {
      project_id: activity.project_id,
      activity_type: activity.action_type,
      activity_id: activity.id,
    })
    setShowDetails(true)
  }


  const getActivityMessage = () => {
    const metadata = activity.action_metadata || {}

    switch (activity.action_type) {
      case 'clip_import': {
        const created = metadata.created || 0
        const updated = metadata.updated || 0
        const skipped = metadata.skipped || 0
        const clipNames = metadata.clip_names || []

        const parts = []
        if (created > 0) parts.push(`${created} created`)
        if (updated > 0) parts.push(`${updated} updated`)
        if (skipped > 0) parts.push(`${skipped} skipped`)

        const message = parts.length > 0 ? `imported ALE: ${parts.join(', ')}` : 'imported ALE file'

        return {
          message,
          details: clipNames.length > 0 ? clipNames.slice(0, 3).join(', ') + (clipNames.length > 3 ? '...' : '') : null,
        }
      }

      case 'otio_clip_link': {
        const count = metadata.clips_count || 0
        const commitMsg = metadata.commit_message || 'timeline commit'

        return {
          message: 'imported OTIO Timeline',
          details: `${count} clip${count !== 1 ? 's' : ''} linked • ${commitMsg}`,
        }
      }

      case 'otio_clip_create': {
        const count = metadata.clips_count || 0
        const commitMsg = metadata.commit_message || 'timeline commit'

        return {
          message: 'imported OTIO Timeline',
          details: `${count} new clip${count !== 1 ? 's' : ''} • ${commitMsg}`,
        }
      }

      case 'clip_delete': {
        const clipName = metadata.clip_name
        return {
          message: `deleted clip${clipName ? ` "${clipName}"` : ''}`,
          details: null,
        }
      }

      case 'clip_update': {
        return {
          message: 'updated clip metadata',
          details: null,
        }
      }

      case 'project_create': {
        const projectName = metadata.project_name
        return {
          message: projectName ? `created project "${projectName}"` : 'created project',
          details: metadata.description || null,
        }
      }

      case 'vfx_shots_detected': {
        const shotsDetected = metadata.shots_detected || 0
        const shotsCreated = metadata.shots_created || 0
        const shotsUpdated = metadata.shots_updated || 0
        const parts = []
        if (shotsCreated > 0) parts.push(`${shotsCreated} created`)
        if (shotsUpdated > 0) parts.push(`${shotsUpdated} updated`)

        const message = `detected ${shotsDetected} VFX shot${shotsDetected !== 1 ? 's' : ''}${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`
        const commitMsg = metadata.commit_message || ''

        return {
          message,
          details: commitMsg || null,
        }
      }

      case 'commit_delete': {
        const commitNumber = metadata.commit_number
        const commitMessage = metadata.message || 'No message'

        return {
          message: commitNumber ? `deleted commit #${commitNumber}` : 'deleted commit',
          details: commitMessage,
        }
      }

      case 'transcode_upload': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const createdShots = metadata.created_shots || []
        const linkedShots = metadata.linked_shots || []
        const errors = metadata.errors || []

        const parts = []
        if (createdShots.length > 0) parts.push(`${createdShots.length} shot${createdShots.length !== 1 ? 's' : ''} created`)
        if (linkedShots.length > 0) parts.push(`${linkedShots.length} shot${linkedShots.length !== 1 ? 's' : ''} linked`)
        if (errors.length > 0) parts.push(`${errors.length} error${errors.length !== 1 ? 's' : ''}`)

        return {
          message: `uploaded transcode ${transcodeId}`,
          details: parts.length > 0 ? parts.join(', ') : 'EDL processed',
        }
      }

      case 'transcode_complete': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const notes = metadata.completion_notes

        return {
          message: `completed transcode ${transcodeId}`,
          details: notes || null,
        }
      }

      case 'transcode_delete': {
        const transcodeId = metadata.transcode_id || 'Unknown'
        const unlinkedCount = metadata.unlinked_count || 0

        return {
          message: `deleted transcode ${transcodeId}`,
          details: unlinkedCount > 0 ? `${unlinkedCount} shot${unlinkedCount !== 1 ? 's' : ''} unlinked` : null,
        }
      }

      case 'timeline_comparison': {
        const fromCommit = metadata.from_commit_number
        const toCommit = metadata.to_commit_number
        const totalChanges = metadata.total_changes || 0
        const vfxChanges = metadata.vfx_changes || 0
        const retranscodeRequired = metadata.retranscode_required || 0

        const parts = []
        if (totalChanges > 0) parts.push(`${totalChanges} change${totalChanges !== 1 ? 's' : ''}`)
        if (vfxChanges > 0) parts.push(`${vfxChanges} VFX shot${vfxChanges !== 1 ? 's' : ''}`)
        if (retranscodeRequired > 0) parts.push(`${retranscodeRequired} re-transcode required`)

        return {
          message: `compared commits #${fromCommit} → #${toCommit}`,
          details: parts.length > 0 ? parts.join(', ') : null,
        }
      }

      case 'comment_created': {
        const commentPreview = metadata.comment_preview || ''
        const commentableName = metadata.commentable_name || 'Unknown'
        const commentableType = metadata.commentable_type || 'item'

        return {
          message: `commented on ${commentableType} "${commentableName}"`,
          details: commentPreview ? `"${commentPreview}"` : null,
        }
      }

      case 'comment_deleted': {
        const commentableName = metadata.commentable_name || 'Unknown'
        const commentableType = metadata.commentable_type || 'item'

        return {
          message: `deleted a comment on ${commentableType} "${commentableName}"`,
          details: null,
        }
      }

      case 'bulk_tag_update': {
        const count = metadata.clips_count || 0
        const addedTags = metadata.added_tags || []
        const removedTags = metadata.removed_tags || []
        const parts = []

        if (addedTags.length > 0) parts.push(`added ${addedTags.join(', ')}`)
        if (removedTags.length > 0) parts.push(`removed ${removedTags.join(', ')}`)

        return {
          message: `updated tags for ${count} clip${count !== 1 ? 's' : ''}`,
          details: parts.length > 0 ? parts.join(' • ') : null,
        }
      }

      case 'bulk_status_update': {
        const count = metadata.clips_count || 0
        const status = metadata.status || 'Unknown'

        return {
          message: `updated status for ${count} clip${count !== 1 ? 's' : ''}`,
          details: `Set to: ${status}`,
        }
      }

      default:
        return {
          message: config?.label || 'performed an action',
          details: null,
        }
    }
  }

  const { message, details } = getActivityMessage()
  const timeAgo = formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })

  // Determine the display name for the user who performed the action
  const isCurrentUser = currentUser?.id === activity.user_id
  const userDisplayName = isCurrentUser
    ? 'You'
    : activity.user?.full_name || 'Someone'

  return (
    <>
      <div
        className="flex items-start gap-3 py-3 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors group"
        onClick={handleActivityClick}
      >
        <div className={`mt-0.5 ${config?.color || 'text-muted-foreground'}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span className="font-medium">{userDisplayName}</span> {message}
          </p>
          {projectName && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {projectName}
            </p>
          )}
          {details && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {details}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        </div>
        <div className="mt-0.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
      <ActivityDetailsDialog
        activity={activity}
        open={showDetails}
        onOpenChange={setShowDetails}
      />
    </>
  )
}

export function ActivityFeed({
  projectId,
  showProjectFilter = false,
  showHeader = true
}: ActivityFeedProps) {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId || null)
  const { data: projects } = useProjects()

  // Use selectedProjectId for filtering when showProjectFilter is true, otherwise use projectId
  const filterProjectId = showProjectFilter ? selectedProjectId : projectId
  const { data: activities, isLoading, error } = useActivityLogs(filterProjectId)

  // Create a map of project IDs to names for quick lookup
  const projectMap = useMemo(() => {
    if (!projects) return {}
    return projects.reduce((acc, project) => {
      acc[project.id] = project.name
      return acc
    }, {} as Record<string, string>)
  }, [projects])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading activity...</p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center gap-2 text-destructive p-4">
          <AlertCircle className="h-5 w-5" />
          <p>Failed to load activity feed</p>
        </div>
      )
    }

    if (!activities || activities.length === 0) {
      return (
        <div className="text-center py-12">
          <Activity className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No activity yet</h3>
          <p className="text-muted-foreground">
            {projectId ? 'Project activities will appear here as you work' : 'Activities will appear here as you work'}
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-1 divide-y divide-border">
        {activities.map((activity) => (
          <ActivityItem
            key={activity.id}
            activity={activity}
            projectName={!projectId ? projectMap[activity.project_id] : undefined}
          />
        ))}
      </div>
    )
  }

  return (
    <Card>
      {showHeader && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activity</CardTitle>
              <CardDescription>
                {projectId ? 'Recent project activity' : 'Recent activity across all projects'}
              </CardDescription>
            </div>
            {showProjectFilter && projects && projects.length > 0 && (
              <Select
                value={selectedProjectId || 'all'}
                onValueChange={(value) => setSelectedProjectId(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
      )}
      <CardContent className={showHeader ? '' : 'pt-6'}>
        {renderContent()}
      </CardContent>
    </Card>
  )
}
